import { type ReactNode } from "react";
import {
  ToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  type SxProps,
  type Theme,
} from "@mui/material";

import { mergeSx } from "ui/merge-sx";

import { borderRadius, colors } from "design-tokens";

export type ToggleButtonOption = { label: ReactNode; value: string };

export type ToggleButtonsProps = {
  className?: string;
  sx?: SxProps<Theme>;
  groupName: string;
  options: string[] | ToggleButtonOption[];
  defaultValue?: string;
  onChange: (value: string) => void;
  value?: string;
  size?: "sm" | "md";
  disabled?: boolean;
};

const SIZE_SX: Record<"sm" | "md", SxProps<Theme>> = {
  sm: { fontSize: "14px", lineHeight: "20px", px: "12px", py: "6px" },
  md: { fontSize: "1rem", lineHeight: 1.625, px: "1rem", py: "0.5rem" },
};

export const ToggleButtonGroup = ({
  value,
  groupName,
  options,
  defaultValue,
  onChange,
  disabled,
  size = "md",
  className,
  sx,
}: ToggleButtonsProps) => {
  const normalizedOptions: ToggleButtonOption[] = options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt,
  );

  const radius = borderRadius.lg;

  const buttonSx: SxProps<Theme> = {
    ...(SIZE_SX[size] as object),
    flex: "auto",
    textTransform: "none",
    fontWeight: "bold",
    border: 0,
    backgroundColor: "grey.50",
    color: "primary.main",
    "[role='alert']": { color: colors.critical[700] },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "secondary.main",
      color: "grey.50",
      "[role='alert']": { color: "currentColor" },
    },
    "&:not(:last-of-type)": {
      borderRight: `1px solid ${colors.neutrals[200]}`,
    },
  };

  return (
    <MuiToggleButtonGroup
      exclusive
      data-testid="toggle-button-group"
      aria-label={groupName}
      value={value ?? defaultValue ?? normalizedOptions[0]?.value}
      disabled={disabled}
      onChange={(_event, next: string | null) => {
        if (next !== null) onChange(next);
      }}
      className={className}
      sx={mergeSx(
        {
          boxShadow: "none",
          borderRadius: radius,
          "& .MuiToggleButton-root.Mui-disabled": { opacity: 0.4, pointerEvents: "none" },
        },
        sx,
      )}
    >
      {normalizedOptions.map(({ label, value: optionValue }) => (
        <ToggleButton key={optionValue} value={optionValue} sx={buttonSx}>
          {label}
        </ToggleButton>
      ))}
    </MuiToggleButtonGroup>
  );
};
